
// Polyfill for crypto.randomUUID in insecure contexts
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
  crypto.randomUUID = () => {
    return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
  };
}

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, BookOpen, Download, Calendar, Upload, FileText, CheckCircle } from 'lucide-react';
import FileUploadInput, { FILE_ACCEPT_TYPES } from '../../components/admin/FileUploadInput';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicationsPage() {
  const [activeTab, setActiveTab] = useState('annual-report');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { isCreator } = useAuth();

  // Data states for each tab
  const [annualReports, setAnnualReports] = useState([]);
  const [ePublications, setEPublications] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [udyanRashmi, setUdyanRashmi] = useState([]);
  const [institutePublications, setInstitutePublications] = useState([]);
  const [priceListItems, setPriceListItems] = useState([]);
  const [priceListPdfUrl, setPriceListPdfUrl] = useState('');
  const [vision2050, setVision2050] = useState(null);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [form, setForm] = useState({});

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      let endpoint = '';

      switch (activeTab) {
        case 'annual-report':
          endpoint = '/api/publications/annual-report?published_only=false';
          const arRes = await axios.get(`${API_URL}${endpoint}`);
          setAnnualReports(arRes.data);
          break;
        case 'e-publications':
          endpoint = '/api/publications/e-publications?published_only=false';
          const epRes = await axios.get(`${API_URL}${endpoint}`);
          setEPublications(epRes.data);
          break;
        case 'newsletter':
          endpoint = '/api/publications/newsletter?published_only=false';
          const nlRes = await axios.get(`${API_URL}${endpoint}`);
          setNewsletters(nlRes.data);
          break;
        case 'udyan-rashmi':
          endpoint = '/api/publications/udyan-rashmi?published_only=false';
          const urRes = await axios.get(`${API_URL}${endpoint}`);
          setUdyanRashmi(urRes.data);
          break;
        case 'cish-publications':
          endpoint = '/api/publications/cish-publications?published_only=false';
          const cpRes = await axios.get(`${API_URL}${endpoint}`);
          setInstitutePublications(cpRes.data.institute_publications || []);
          setPriceListItems(cpRes.data.price_list_items || []);
          setPriceListPdfUrl(cpRes.data.price_list_pdf_url || '');
          break;
        case 'vision2050':
          try {
            endpoint = '/api/publications/vision2050?published_only=false';
            const v2050Res = await axios.get(`${API_URL}${endpoint}`);
            setVision2050(v2050Res.data);
          } catch (error) {
            if (error.response?.status === 404) {
              setVision2050(null);
            }
          }
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load publications data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      setSaving(true);
      let endpoint = '';
      let method = editingItem ? 'put' : 'post';

      switch (activeTab) {
        case 'annual-report':
          endpoint = editingItem
            ? `/api/publications/annual-report/${editingItem.id}`
            : '/api/publications/annual-report';
          break;
        case 'e-publications':
          endpoint = editingItem
            ? `/api/publications/e-publications/${editingItem.id}`
            : '/api/publications/e-publications';
          break;
        case 'newsletter':
          endpoint = editingItem
            ? `/api/publications/newsletter/${editingItem.id}`
            : '/api/publications/newsletter';
          break;
        case 'udyan-rashmi':
          endpoint = editingItem
            ? `/api/publications/udyan-rashmi/${editingItem.id}`
            : '/api/publications/udyan-rashmi';
          break;
        case 'cish-publications':
          // Handle separately
          break;
        case 'vision2050':
          endpoint = '/api/publications/vision2050';
          method = 'post';
          break;
      }

      await axios[method](`${API_URL}${endpoint}`, data, { headers: getAuthHeaders() });
      toast.success(editingItem ? 'Updated successfully' : (isCreator ? 'Submitted for approval' : 'Created successfully'));
      setOpenDialog(false);
      setEditingItem(null);
      setForm({});
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      let endpoint = '';
      switch (activeTab) {
        case 'annual-report':
          endpoint = `/api/publications/annual-report/${id}`;
          break;
        case 'e-publications':
          endpoint = `/api/publications/e-publications/${id}`;
          break;
        case 'newsletter':
          endpoint = `/api/publications/newsletter/${id}`;
          break;
        case 'udyan-rashmi':
          endpoint = `/api/publications/udyan-rashmi/${id}`;
          break;
      }

      await axios.delete(`${API_URL}${endpoint}`, { headers: getAuthHeaders() });
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    }
  };

  const handleFileUpload = async (id, file, uploadType) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      let endpoint = '';
      switch (uploadType) {
        case 'annual-report':
          endpoint = `/api/publications/annual-report/${id}/upload-pdf`;
          break;
        case 'e-publications':
          endpoint = `/api/publications/e-publications/${id}/upload-pdf`;
          break;
        case 'newsletter':
          endpoint = `/api/publications/newsletter/${id}/upload-pdf`;
          break;
        case 'udyan-rashmi':
          endpoint = `/api/publications/udyan-rashmi/${id}/upload-pdf`;
          break;
        case 'institute':
          endpoint = `/api/publications/cish-publications/institute/${id}/upload-pdf`;
          break;
        case 'vision2050':
          endpoint = `/api/publications/vision2050/upload-pdf`;
          break;
        case 'price-list-pdf':
          endpoint = `/api/publications/cish-publications/upload-price-list-pdf`;
          break;
      }

      await axios.post(`${API_URL}${endpoint}`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('PDF uploaded successfully');
      fetchData(); // Refresh to show updated file info
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    }
  };

  const openAddDialog = () => {
    setEditingItem(null);
    setForm({});
    setOpenDialog(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setForm(item);
    setOpenDialog(true);
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <BookOpen className="w-8 h-8 text-[#0E6D2B]" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Publications Management</h1>
            <p className="text-sm text-gray-600">Manage all publication content from one place</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-6 w-full mb-6">
            <TabsTrigger value="annual-report">Annual Report</TabsTrigger>
            <TabsTrigger value="e-publications">E-Publications</TabsTrigger>
            <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
            <TabsTrigger value="udyan-rashmi">Udyan Rashmi</TabsTrigger>
            <TabsTrigger value="cish-publications">CISH Publications</TabsTrigger>
            <TabsTrigger value="vision2050">Vision 2050</TabsTrigger>
          </TabsList>

          {/* TAB A: Annual Report */}
          <TabsContent value="annual-report">
            <AnnualReportTab
              data={annualReports}
              onAdd={openAddDialog}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onFileUpload={handleFileUpload}
              loading={loading}
            />
          </TabsContent>

          {/* TAB B: E-Publications */}
          <TabsContent value="e-publications">
            <EPublicationsTab
              data={ePublications}
              onAdd={openAddDialog}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onFileUpload={handleFileUpload}
              loading={loading}
            />
          </TabsContent>

          {/* TAB C: Newsletter */}
          <TabsContent value="newsletter">
            <NewsletterTab
              data={newsletters}
              onAdd={openAddDialog}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onFileUpload={handleFileUpload}
              loading={loading}
            />
          </TabsContent>

          {/* TAB D: Udyan Rashmi */}
          <TabsContent value="udyan-rashmi">
            <UdyanRashmiTab
              data={udyanRashmi}
              onAdd={openAddDialog}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onFileUpload={handleFileUpload}
              loading={loading}
            />
          </TabsContent>

          {/* TAB E: CISH Publications */}
          <TabsContent value="cish-publications">
            <CISHPublicationsTab
              institutePublications={institutePublications}
              priceListItems={priceListItems}
              priceListPdfUrl={priceListPdfUrl}
              onRefresh={fetchData}
              onFileUpload={handleFileUpload}
              getAuthHeaders={getAuthHeaders}
            />
          </TabsContent>

          {/* TAB F: Vision 2050 */}
          <TabsContent value="vision2050">
            <Vision2050Tab
              data={vision2050}
              onSave={handleSave}
              onFileUpload={handleFileUpload}
              loading={loading}
            />
          </TabsContent>
        </Tabs>

        {/* Generic Dialog for Add/Edit */}
        {openDialog && activeTab !== 'cish-publications' && activeTab !== 'vision2050' && (
          <PublicationDialog
            open={openDialog}
            onOpenChange={setOpenDialog}
            type={activeTab}
            item={editingItem}
            onSave={handleSave}
            saving={saving}
          />
        )}
      </div>
    </AdminLayout>
  );
}

// Component for each tab
function AnnualReportTab({ data, onAdd, onEdit, onDelete, onFileUpload, loading }) {
  const fileInputRef = useRef(null);
  const [uploadingId, setUploadingId] = useState(null);

  const handleFileSelect = async (e, itemId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploadingId(itemId);
    await onFileUpload(itemId, file, 'annual-report');
    setUploadingId(null);
    e.target.value = ''; // Reset input
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">{data.length} reports</p>
        <Button onClick={onAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Add Report
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Year</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>PDF Status</TableHead>
            <TableHead>Pages</TableHead>
            <TableHead>Latest</TableHead>
            <TableHead>Coming Soon</TableHead>
            <TableHead>Published</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.year}</TableCell>
              <TableCell>{item.title}</TableCell>
              <TableCell>
                {(item.file_name || item.pdf_url) ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-xs truncate max-w-[150px]">{item.file_name || (item.pdf_url ? item.pdf_url.split('/').pop() : 'PDF Uploaded')}</span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">No PDF</span>
                )}
              </TableCell>
              <TableCell>{item.pages_count || '-'}</TableCell>
              <TableCell>{item.is_latest ? '✅' : '-'}</TableCell>
              <TableCell>{item.coming_soon ? '🔜' : '-'}</TableCell>
              <TableCell>{item.is_published ? '✅' : '❌'}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById(`file-${item.id}`).click()}
                    disabled={uploadingId === item.id}
                  >
                    {uploadingId === item.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </Button>
                  <input
                    id={`file-${item.id}`}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e, item.id)}
                  />
                  <Button variant="outline" size="sm" onClick={() => onEdit(item)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onDelete(item.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Similar components for other tabs
function EPublicationsTab({ data, onAdd, onEdit, onDelete, onFileUpload, loading }) {
  const [uploadingId, setUploadingId] = useState(null);

  const handleFileSelect = async (e, itemId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploadingId(itemId);
    await onFileUpload(itemId, file, 'e-publications');
    setUploadingId(null);
    e.target.value = '';
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">{data.length} publications</p>
        <Button onClick={onAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Add Publication
        </Button>
      </div>

      <div className="grid gap-4">
        {data.map((item) => (
          <div key={item.id} className="border rounded-lg p-4 flex justify-between items-start">
            <div className="flex-1">
              <h3 className="font-bold">{item.title}</h3>
              <p className="text-sm text-gray-600">Year: {item.year} • Pages: {item.pages || 'N/A'}</p>
              {item.short_description && <p className="text-sm text-gray-500 mt-1">{item.short_description}</p>}
              {item.file_name && (
                <div className="flex items-center gap-1 text-green-600 mt-2">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs">{item.file_name}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById(`file-epub-${item.id}`).click()}
                disabled={uploadingId === item.id}
                title="Upload PDF"
              >
                {uploadingId === item.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
              </Button>
              <input
                id={`file-epub-${item.id}`}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => handleFileSelect(e, item.id)}
              />
              <Button variant="outline" size="sm" onClick={() => onEdit(item)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDelete(item.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsletterTab({ data, onAdd, onEdit, onDelete, onFileUpload, loading }) {
  const [uploadingId, setUploadingId] = useState(null);

  const handleFileSelect = async (e, itemId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploadingId(itemId);
    await onFileUpload(itemId, file, 'newsletter');
    setUploadingId(null);
    e.target.value = '';
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">{data.length} newsletters</p>
        <Button onClick={onAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Add Newsletter
        </Button>
      </div>

      <div className="grid gap-4">
        {data.map((item) => (
          <div key={item.id} className="border rounded-lg p-4 flex justify-between items-start">
            <div className="flex-1">
              <h3 className="font-bold">{item.title}</h3>
              <p className="text-sm text-gray-600">{item.volume_issue} • {item.period_label} • {item.pages} pages</p>
              {item.file_name && (
                <div className="flex items-center gap-1 text-green-600 mt-2">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs">{item.file_name}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById(`file-nl-${item.id}`).click()}
                disabled={uploadingId === item.id}
                title="Upload PDF"
              >
                {uploadingId === item.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
              </Button>
              <input
                id={`file-nl-${item.id}`}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => handleFileSelect(e, item.id)}
              />
              <Button variant="outline" size="sm" onClick={() => onEdit(item)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDelete(item.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UdyanRashmiTab({ data, onAdd, onEdit, onDelete, onFileUpload, loading }) {
  const [uploadingId, setUploadingId] = useState(null);

  const handleFileSelect = async (e, itemId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploadingId(itemId);
    await onFileUpload(itemId, file, 'udyan-rashmi');
    setUploadingId(null);
    e.target.value = '';
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">{data.length} publications</p>
        <Button onClick={onAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Add Publication
        </Button>
      </div>

      <div className="grid gap-4">
        {data.map((item) => (
          <div key={item.id} className="border rounded-lg p-4 flex justify-between items-start">
            <div className="flex-1">
              <h3 className="font-bold">{item.title}</h3>
              <p className="text-sm text-gray-600">{item.volume_issue} • {item.period_label} • {item.pages} pages</p>
              {item.file_name && (
                <div className="flex items-center gap-1 text-green-600 mt-2">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs">{item.file_name}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById(`file-ur-${item.id}`).click()}
                disabled={uploadingId === item.id}
                title="Upload PDF"
              >
                {uploadingId === item.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
              </Button>
              <input
                id={`file-ur-${item.id}`}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => handleFileSelect(e, item.id)}
              />
              <Button variant="outline" size="sm" onClick={() => onEdit(item)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDelete(item.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CISHPublicationsTab({ institutePublications, priceListItems, priceListPdfUrl, onRefresh, onFileUpload, getAuthHeaders }) {
  const { isCreator } = useAuth();
  const [showInstPubDialog, setShowInstPubDialog] = useState(false);
  const [showPriceListDialog, setShowPriceListDialog] = useState(false);
  const [editingInstPub, setEditingInstPub] = useState(null);
  const [editingPriceItem, setEditingPriceItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingPriceList, setUploadingPriceList] = useState(false);

  const [instPubForm, setInstPubForm] = useState({
    title: '', year: new Date().getFullYear(), short_description: '', is_published: true
  });

  const [priceItemForm, setPriceItemForm] = useState({
    serial_no: priceListItems.length + 1, name_of_publication: '', price: 0, shipping_cost: 0, total_cost: 0, note: ''
  });

  const handleFileSelect = async (e, itemId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploadingId(itemId);
    await onFileUpload(itemId, file, 'institute');
    setUploadingId(null);
    e.target.value = '';
  };

  const handlePriceListPdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploadingPriceList(true);
    await onFileUpload(null, file, 'price-list-pdf');
    setUploadingPriceList(false);
    e.target.value = '';
  };

  const handleSaveInstPub = async () => {
    try {
      setSaving(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const endpoint = editingInstPub
        ? `${API_URL}/api/publications/cish-publications/institute/${editingInstPub.id}`
        : `${API_URL}/api/publications/cish-publications/institute`;
      const method = editingInstPub ? 'put' : 'post';

      await axios[method](endpoint, instPubForm, { headers: getAuthHeaders() });
      toast.success(editingInstPub ? 'Updated successfully' : (isCreator ? 'Submitted for approval' : 'Created successfully'));
      setShowInstPubDialog(false);
      setEditingInstPub(null);
      setInstPubForm({ title: '', year: new Date().getFullYear(), short_description: '', is_published: true });
      onRefresh();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInstPub = async (id) => {
    if (!confirm('Delete this publication?')) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      await axios.delete(`${API_URL}/api/publications/cish-publications/institute/${id}`, { headers: getAuthHeaders() });
      toast.success('Deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSavePriceItem = async () => {
    try {
      setSaving(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const formData = { ...priceItemForm, total_cost: priceItemForm.price + priceItemForm.shipping_cost };
      const endpoint = editingPriceItem
        ? `${API_URL}/api/publications/cish-publications/price-list/${editingPriceItem.id}`
        : `${API_URL}/api/publications/cish-publications/price-list`;
      const method = editingPriceItem ? 'put' : 'post';

      await axios[method](endpoint, formData, { headers: getAuthHeaders() });
      toast.success(editingPriceItem ? 'Updated successfully' : (isCreator ? 'Submitted for approval' : 'Created successfully'));
      setShowPriceListDialog(false);
      setEditingPriceItem(null);
      setPriceItemForm({ serial_no: priceListItems.length + 1, name_of_publication: '', price: 0, shipping_cost: 0, total_cost: 0, note: '' });
      onRefresh();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePriceItem = async (id) => {
    if (!confirm('Delete this item?')) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      await axios.delete(`${API_URL}/api/publications/cish-publications/price-list/${id}`, { headers: getAuthHeaders() });
      toast.success('Deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-8">
      {/* Institute Publications */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Institute Publications ({institutePublications.length})</h3>
          <Button onClick={() => { setEditingInstPub(null); setInstPubForm({ title: '', year: new Date().getFullYear(), short_description: '', is_published: true }); setShowInstPubDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Publication
          </Button>
        </div>
        <div className="grid gap-3">
          {institutePublications.map((item) => (
            <div key={item.id} className="border rounded-lg p-4 flex justify-between items-start bg-white">
              <div className="flex-1">
                <h4 className="font-bold">{item.title}</h4>
                <p className="text-sm text-gray-600">Year: {item.year}</p>
                {item.short_description && <p className="text-sm text-gray-500 mt-1">{item.short_description}</p>}
                {item.file_name && (
                  <div className="flex items-center gap-1 text-green-600 mt-2">
                    <FileText className="w-4 h-4" />
                    <span className="text-xs">{item.file_name}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById(`file-inst-${item.id}`).click()}
                  disabled={uploadingId === item.id}
                  title="Upload PDF"
                >
                  {uploadingId === item.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                </Button>
                <input
                  id={`file-inst-${item.id}`}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, item.id)}
                />
                <Button variant="outline" size="sm" onClick={() => { setEditingInstPub(item); setInstPubForm(item); setShowInstPubDialog(true); }}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDeleteInstPub(item.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Price List */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">ICAR-CISH Price List ({priceListItems.length})</h3>
          <Button onClick={() => { setEditingPriceItem(null); setPriceItemForm({ serial_no: priceListItems.length + 1, name_of_publication: '', price: 0, shipping_cost: 0, total_cost: 0, note: '' }); setShowPriceListDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>S.No.</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Price (₹)</TableHead>
              <TableHead>Shipping (₹)</TableHead>
              <TableHead>Total (₹)</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {priceListItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.serial_no}</TableCell>
                <TableCell>{item.name_of_publication}</TableCell>
                <TableCell>₹{item.price.toFixed(2)}</TableCell>
                <TableCell>₹{item.shipping_cost.toFixed(2)}</TableCell>
                <TableCell>₹{item.total_cost.toFixed(2)}</TableCell>
                <TableCell className="text-sm text-gray-600">{item.note || '-'}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingPriceItem(item); setPriceItemForm(item); setShowPriceListDialog(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDeletePriceItem(item.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Price List PDF Upload */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold mb-2">Price List PDF (Downloadable)</h4>
        <p className="text-sm text-gray-600 mb-3">Upload a PDF file containing the complete price list for download</p>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => document.getElementById('price-list-pdf-upload').click()}
            disabled={uploadingPriceList}
          >
            {uploadingPriceList ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Price List PDF
              </>
            )}
          </Button>
          <input
            id="price-list-pdf-upload"
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={handlePriceListPdfUpload}
          />
          {priceListPdfUrl && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              PDF Uploaded
            </span>
          )}
        </div>
      </div>

      {/* Institute Publication Dialog */}
      <Dialog open={showInstPubDialog} onOpenChange={setShowInstPubDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingInstPub ? 'Edit' : 'Add'} Institute Publication</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input required value={instPubForm.title} onChange={(e) => setInstPubForm({ ...instPubForm, title: e.target.value })} />
            </div>
            <div>
              <Label>Year *</Label>
              <Input required type="number" value={instPubForm.year} onChange={(e) => setInstPubForm({ ...instPubForm, year: parseInt(e.target.value) })} />
            </div>
            <div>
              <Label>Short Description</Label>
              <Textarea value={instPubForm.short_description || ''} onChange={(e) => setInstPubForm({ ...instPubForm, short_description: e.target.value })} />
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={instPubForm.is_published} onChange={(e) => setInstPubForm({ ...instPubForm, is_published: e.target.checked })} />
              <span>Published</span>
            </label>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveInstPub} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save
              </Button>
              <Button variant="outline" onClick={() => setShowInstPubDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price List Item Dialog */}
      <Dialog open={showPriceListDialog} onOpenChange={setShowPriceListDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingPriceItem ? 'Edit' : 'Add'} Price List Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Serial No. *</Label>
              <Input required type="number" value={priceItemForm.serial_no} onChange={(e) => setPriceItemForm({ ...priceItemForm, serial_no: parseInt(e.target.value) })} />
            </div>
            <div>
              <Label>Name of Publication *</Label>
              <Input required value={priceItemForm.name_of_publication} onChange={(e) => setPriceItemForm({ ...priceItemForm, name_of_publication: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹) *</Label>
                <Input required type="number" step="0.01" value={priceItemForm.price} onChange={(e) => setPriceItemForm({ ...priceItemForm, price: parseFloat(e.target.value) })} />
              </div>
              <div>
                <Label>Shipping Cost (₹) *</Label>
                <Input required type="number" step="0.01" value={priceItemForm.shipping_cost} onChange={(e) => setPriceItemForm({ ...priceItemForm, shipping_cost: parseFloat(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label>Total Cost (Auto-calculated)</Label>
              <Input disabled value={(priceItemForm.price + priceItemForm.shipping_cost).toFixed(2)} className="bg-gray-100" />
            </div>
            <div>
              <Label>Note</Label>
              <Input value={priceItemForm.note || ''} onChange={(e) => setPriceItemForm({ ...priceItemForm, note: e.target.value })} placeholder="e.g., Full color, 120 pages" />
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSavePriceItem} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save
              </Button>
              <Button variant="outline" onClick={() => setShowPriceListDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Vision2050Tab({ data, onSave, onFileUpload, loading }) {
  const [form, setForm] = useState({
    title: data?.title || 'Vision 2050',
    content_html: data?.content_html || '',
    pdf_url: data?.pdf_url || '',
    show_download: data?.show_download !== false,
    is_published: data?.is_published !== false
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const { isCreator } = useAuth(); // Assuming we might need this later, but for delete we need token manually or via axios helper

  useEffect(() => {
    if (data) {
      setForm({
        title: data.title || 'Vision 2050',
        content_html: data.content_html || '',
        pdf_url: data.pdf_url || '',
        show_download: data.show_download !== false,
        is_published: data.is_published !== false
      });
    }
  }, [data]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploadingPdf(true);
    await onFileUpload(null, file, 'vision2050');
    setUploadingPdf(false);
    e.target.value = '';
  };

  const handleDeletePdf = async () => {
    if (!window.confirm('Are you sure you want to delete the Vision 2050 PDF?')) return;

    try {
      setUploadingPdf(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('cish_token');

      await axios.delete(`${API_URL}/api/publications/vision2050/file`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('File deleted successfully');
      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete file');
      setUploadingPdf(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Title *</Label>
        <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      </div>
      <div>
        <Label>Content HTML * (Use CKEditor or HTML)</Label>
        <Textarea required rows={10} value={form.content_html} onChange={(e) => setForm({ ...form, content_html: e.target.value })} placeholder="Enter HTML content here..." />
      </div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.show_download} onChange={(e) => setForm({ ...form, show_download: e.target.checked })} />
          <span>Show Download Button</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
          <span>Published</span>
        </label>
      </div>

      {/* PDF Upload Section */}
      <div className="border-t pt-4 mt-4">
        <Label>Vision 2050 PDF File</Label>
        <FileUploadInput
          field={{ name: 'pdf_url', type: 'file', fileType: 'document' }}
          value={null} // Vision2050Tab form handling is a bit manual, assuming direct upload for now or adapt if needed. 
          // Actually, FileUploadInput expects 'value' to be the file object(s) for NEW uploads. 
          // But here we might just want to use it as a uploader that updates 'form.pdf_url'.
          // Simplest way: Use it to handle the file selection and generic upload, and update form.pdf_url
          formData={form}
          setFormData={setForm}
          existingUrl={form.pdf_url}
          onDeleteServerFile={() => {
            if (window.confirm('Delete PDF?')) {
              setForm({ ...form, pdf_url: '' });
              return true;
            }
            return false;
          }}
          // We can let FileUploadInput handle the upload internally if we pass 'formData' and 'setFormData' correctly? 
          // No, FileUploadInput doesn't auto-upload to an arbitrary URL unless modified.
          // It uploads to generic endpoint if we use the helper logic pattern.
          // Let's use the pattern: 
          onChange={async (files) => {
            if (files && files.length > 0) {
              setUploadingPdf(true);
              try {
                const formData = new FormData();
                formData.append('file', files[0]);
                formData.append('subfolder', 'vision2050');
                const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                const token = localStorage.getItem('cish_token');
                const res = await axios.post(`${API_URL}/api/files/upload?subfolder=vision2050`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
                let pdfUrl = res.data.file_path;
                if (pdfUrl) {
                  // Ensure clean path and prepend /uploads/ for direct Nginx serving
                  if (pdfUrl.includes('data/files/')) pdfUrl = pdfUrl.replace('data/files/', '');
                  if (!pdfUrl.startsWith('/uploads/')) pdfUrl = '/uploads/' + (pdfUrl.startsWith('/') ? pdfUrl.slice(1) : pdfUrl);
                }
                setForm(prev => ({ ...prev, pdf_url: pdfUrl }));
                toast.success('PDF uploaded');
              } catch (e) { toast.error('Upload failed'); }
              finally { setUploadingPdf(false); }
            }
          }}
        />
      </div>

      {/* Manual URL Input as fallback */}
      <div>
        <label className="block text-sm font-medium mb-1">Or Enter PDF URL Manually</label>
        <input
          type="text"
          value={form.pdf_url || ''}
          onChange={(e) => setForm({ ...form, pdf_url: e.target.value })}
          placeholder="/uploads/publications/vision/filename.pdf"
          className="w-full px-3 py-2 border rounded"
        />
        <p className="text-xs text-gray-500 mt-1">
          Example: /uploads/publications/vision/your-file.pdf
        </p>
      </div>

      <Button type="submit">Save Vision 2050</Button>
    </form>
  );
}

// Generic dialog component
function PublicationDialog({ open, onOpenChange, type, item, onSave, saving }) {
  const [form, setForm] = useState(item || {});
  const [fileData, setFileData] = useState({});
  const [localSaving, setLocalSaving] = useState(false);

  useEffect(() => {
    setForm(item || {});
    setFileData({});
  }, [item, open]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const uploadFile = async (file, subfolder) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('subfolder', subfolder);
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const res = await axios.post(`${API_URL}/api/files/upload?subfolder=${subfolder}`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
    
    // Return the file_path, stripping 'data/files/' prefix to work with /api/uploads mount
    let filePath = res.data.file_path;
    if (filePath) {
      if (filePath.includes('data/files/')) filePath = filePath.replace('data/files/', '');
      if (!filePath.startsWith('/uploads/')) filePath = '/uploads/' + (filePath.startsWith('/') ? filePath.slice(1) : filePath);
    }
    return filePath;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalSaving(true);
    try {
      let updatedForm = { ...form };

      // Upload thumbnail if present
      if (fileData.thumbnail_url) {
        const fileToUpload = Array.isArray(fileData.thumbnail_url) ? fileData.thumbnail_url[0] : fileData.thumbnail_url;
        const url = await uploadFile(fileToUpload, 'publications/thumbnails');
        if (url) updatedForm.thumbnail_url = url;
      }

      // Upload PDF if present
      if (fileData.pdf_url) {
        const fileToUpload = Array.isArray(fileData.pdf_url) ? fileData.pdf_url[0] : fileData.pdf_url;
        const url = await uploadFile(fileToUpload, 'publications/pdfs');
        if (url) {
          updatedForm.pdf_url = url;
          if (fileToUpload && fileToUpload.name) {
            updatedForm.file_name = fileToUpload.name;
          }
        }
      }

      onSave(updatedForm);
    } catch (error) {
      console.error(error);
      toast.error('File upload failed');
    } finally {
      setLocalSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit' : 'Add'} {type.replace('-', ' ')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Common fields based on type */}
          {type === 'annual-report' && (
            <>
              <div>
                <Label>Year *</Label>
                <Input required value={form.year || ''} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2023-24" />
              </div>
              <div>
                <Label>Title *</Label>
                <Input required value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>Pages Count</Label>
                <Input type="number" value={form.pages_count || ''} onChange={(e) => setForm({ ...form, pages_count: parseInt(e.target.value) })} />
              </div>

              {/* Thumbnail Image Upload */}
              <div className="space-y-2">
                <Label>Thumbnail Image (Optional)</Label>
                <FileUploadInput
                  field={{ name: 'thumbnail_url', type: 'file', fileType: 'image' }}
                  value={fileData.thumbnail_url}
                  existingUrl={form.thumbnail_url}
                  onChange={(files) => setFileData({ ...fileData, thumbnail_url: files })}
                  onDeleteServerFile={() => { setForm({ ...form, thumbnail_url: '' }); return true; }}
                />
                <div className="mt-2">
                   <Label className="text-xs text-gray-500">OR Enter External Image URL</Label>
                   <Input 
                      placeholder="https://example.com/image.jpg" 
                      value={form.thumbnail_url || ''} 
                      onChange={(e) => setForm({...form, thumbnail_url: e.target.value})} 
                   />
                </div>
              </div>

              {/* PDF Upload */}
              <div className="space-y-2">
                <Label>PDF File</Label>
                <FileUploadInput
                  field={{ name: 'pdf_url', type: 'file', fileType: 'document' }}
                  value={fileData.pdf_url}
                  existingUrl={form.pdf_url}
                  onChange={(files) => setFileData({ ...fileData, pdf_url: files })}
                  onDeleteServerFile={() => { setForm({ ...form, pdf_url: '' }); return true; }}
                />
                <div className="mt-2">
                   <Label className="text-xs text-gray-500">OR Enter External PDF URL / Path (for large files &gt; 50MB)</Label>
                   <Input 
                      placeholder="https://example.com/file.pdf or /data/files/large.pdf" 
                      value={form.pdf_url || ''} 
                      onChange={(e) => setForm({...form, pdf_url: e.target.value})} 
                   />
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.is_latest || false} onChange={(e) => setForm({ ...form, is_latest: e.target.checked })} />
                  <span>Mark as Latest</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.coming_soon || false} onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })} />
                  <span>Coming Soon</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.is_published !== false} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
                  <span>Published</span>
                </label>
              </div>
            </>
          )}

          {type === 'e-publications' && (
            <>
              <div>
                <Label>Title *</Label>
                <Input required value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <Label>Year *</Label>
                <Input required type="number" value={form.year || ''} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Pages</Label>
                <Input type="number" value={form.pages || ''} onChange={(e) => setForm({ ...form, pages: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Short Description</Label>
                <Textarea value={form.short_description || ''} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
              </div>

              {/* Thumbnail Image Upload */}
              <div className="space-y-2">
                <Label>Thumbnail Image (Optional)</Label>
                <FileUploadInput
                  field={{ name: 'thumbnail_url', type: 'file', fileType: 'image' }}
                  value={fileData.thumbnail_url}
                  existingUrl={form.thumbnail_url}
                  onChange={(files) => setFileData({ ...fileData, thumbnail_url: files })}
                  onDeleteServerFile={() => { setForm({ ...form, thumbnail_url: '' }); return true; }}
                />
                <div className="mt-2">
                   <Label className="text-xs text-gray-500">OR Enter External Image URL</Label>
                   <Input 
                      placeholder="https://example.com/image.jpg" 
                      value={form.thumbnail_url || ''} 
                      onChange={(e) => setForm({...form, thumbnail_url: e.target.value})} 
                   />
                </div>
              </div>

              {/* PDF Upload */}
              <div className="space-y-2">
                <Label>PDF File</Label>
                <FileUploadInput
                  field={{ name: 'pdf_url', type: 'file', fileType: 'document' }}
                  value={fileData.pdf_url}
                  existingUrl={form.pdf_url}
                  onChange={(files) => setFileData({ ...fileData, pdf_url: files })}
                  onDeleteServerFile={() => { setForm({ ...form, pdf_url: '' }); return true; }}
                />
                <div className="mt-2">
                   <Label className="text-xs text-gray-500">OR Enter External PDF URL / Path (for large files &gt; 50MB)</Label>
                   <Input 
                      placeholder="https://example.com/file.pdf or /data/files/large.pdf" 
                      value={form.pdf_url || ''} 
                      onChange={(e) => setForm({...form, pdf_url: e.target.value})} 
                   />
                </div>
              </div>

              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_published !== false} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
                <span>Published</span>
              </label>
            </>
          )}

          {(type === 'newsletter' || type === 'udyan-rashmi') && (
            <>
              <div>
                <Label>Title *</Label>
                <Input required value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <Label>Volume/Issue *</Label>
                <Input required value={form.volume_issue || ''} onChange={(e) => setForm({ ...form, volume_issue: e.target.value })} placeholder="Vol. 25, Issue 1" />
              </div>
              <div>
                <Label>Period Label *</Label>
                <Input required value={form.period_label || ''} onChange={(e) => setForm({ ...form, period_label: e.target.value })} placeholder="Jan-Mar 2025" />
              </div>
              <div>
                <Label>Pages</Label>
                <Input type="number" value={form.pages || ''} onChange={(e) => setForm({ ...form, pages: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Short Description</Label>
                <Textarea value={form.short_description || ''} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
              </div>

              {/* Thumbnail Image Upload */}
              <div className="space-y-2">
                <Label>Thumbnail Image (Optional)</Label>
                <FileUploadInput
                  field={{ name: 'thumbnail_url', type: 'file', fileType: 'image' }}
                  value={fileData.thumbnail_url}
                  existingUrl={form.thumbnail_url}
                  onChange={(files) => setFileData({ ...fileData, thumbnail_url: files })}
                  onDeleteServerFile={() => { setForm({ ...form, thumbnail_url: '' }); return true; }}
                />
                <div className="mt-2">
                   <Label className="text-xs text-gray-500">OR Enter External Image URL</Label>
                   <Input 
                      placeholder="https://example.com/image.jpg" 
                      value={form.thumbnail_url || ''} 
                      onChange={(e) => setForm({...form, thumbnail_url: e.target.value})} 
                   />
                </div>
              </div>

              {/* PDF Upload */}
              <div className="space-y-2">
                <Label>PDF File</Label>
                <FileUploadInput
                  field={{ name: 'pdf_url', type: 'file', fileType: 'document' }}
                  value={fileData.pdf_url}
                  existingUrl={form.pdf_url}
                  onChange={(files) => setFileData({ ...fileData, pdf_url: files })}
                  onDeleteServerFile={() => { setForm({ ...form, pdf_url: '' }); return true; }}
                />
                <div className="mt-2">
                   <Label className="text-xs text-gray-500">OR Enter External PDF URL / Path (for large files &gt; 50MB)</Label>
                   <Input 
                      placeholder="https://example.com/file.pdf or /data/files/large.pdf" 
                      value={form.pdf_url || ''} 
                      onChange={(e) => setForm({...form, pdf_url: e.target.value})} 
                   />
                </div>
              </div>

              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_published !== false} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
                <span>Published</span>
              </label>
            </>
          )}

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={saving || localSaving}>
              {(saving || localSaving) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
